/**
 * @file components.tsx
 * @description Shared UI building blocks of the internal CRM part: buttons, page layout,
 * pagination, modals, loading/empty states and the file records grid (documents, contracts).
 */

import React, { useState, useEffect } from 'react';
import {
    Eye,
    RefreshCw,
    List,
    FileText,
    Trash2,
    ChevronLeft,
    ChevronRight,
    Loader2,
    XCircle,
    CheckCircle2,
    Info,
    AlertTriangle,
    Upload,
    Search,
} from 'lucide-react';

/* --- HOOKS --- */

/** Tracks whether the viewport is narrower than the given breakpoint. */
export function useIsMobile(breakpoint = 1024): boolean {
    const [isMobile, setIsMobile] = useState<boolean>(() => window.innerWidth < breakpoint);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < breakpoint);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [breakpoint]);
    return isMobile;
}

/** Returns `value` after it has not changed for `delay` ms (search inputs). */
export function useDebouncedValue<T>(value: T, delay = 350): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);
    return debounced;
}

/* --- BUTTONS --- */

type ButtonVariant = 'primary' | 'secondary' | 'darkBlue' | 'danger' | 'success' | 'outline';

interface AppButtonProps {
    children: React.ReactNode;
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    variant?: ButtonVariant;
    className?: string;
    disabled?: boolean;
    type?: 'button' | 'submit';
    title?: string;
}

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
    primary: 'bg-[#3b82f6] text-white hover:bg-[#2563eb]',
    secondary: 'bg-[#64748b] text-white hover:bg-[#475569]',
    darkBlue: 'bg-[#1e3a8a] text-white hover:bg-[#172554]',
    danger: 'bg-[#ef4444] text-white hover:bg-[#dc2626]',
    success: 'bg-[#22c55e] text-white hover:bg-[#16a34a]',
    outline: 'border-2 border-[#e2e8f0] text-[#64748b] bg-white hover:bg-slate-50 hover:border-slate-300',
};

export const AppButton = ({ children, onClick, variant = 'primary', className = '', disabled = false, type = 'button', title }: AppButtonProps) => (
    <button
        disabled={disabled}
        type={type}
        title={title}
        onClick={onClick}
        className={`px-5 py-2.5 rounded-xl font-bold text-base transition-all active:scale-95 flex items-center justify-center gap-2 shadow-sm ${BUTTON_VARIANTS[variant]} ${disabled ? 'opacity-30 cursor-not-allowed shadow-none' : ''} ${className}`}
    >
        {children}
    </button>
);

/* --- LAYOUT --- */

interface MainLayoutProps {
    title: string;
    subtitle?: string;
    actions?: React.ReactNode;
    children: React.ReactNode;
}

/** Common page frame of every CRM module: eyebrow, title, optional subtitle and actions. */
export const MainLayout = ({ title, subtitle, actions, children }: MainLayoutProps) => (
    <div className="w-full max-w-[1300px] mx-auto text-left fade-in">
        <header className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
                <div className="flex items-center gap-3 mb-3">
                    <div className="h-1 w-8 bg-[#3b82f6] rounded-full"></div>
                    <span className="text-[#3b82f6] font-bold text-xs tracking-widest uppercase leading-none">B2B Insight CRM</span>
                </div>
                <h2 className="text-3xl lg:text-4xl font-black text-slate-900 tracking-tight leading-none">{title}</h2>
                {subtitle && <p className="mt-3 text-base text-slate-500 font-medium max-w-3xl">{subtitle}</p>}
            </div>
            {actions && <div className="flex flex-wrap gap-3">{actions}</div>}
        </header>
        {children}
    </div>
);

/* --- TOOLBAR PARTS --- */

export const SearchInput = ({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) => (
    <div className="relative w-full lg:max-w-xl">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <input
            type="search"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            aria-label={placeholder}
            className="w-full pl-12 pr-4 py-3 bg-slate-50 border-2 border-transparent focus:bg-white focus:border-blue-500 rounded-2xl outline-none transition-all font-semibold text-slate-700"
        />
    </div>
);

export const PageSizeSelect = ({ value, onChange }: { value: number; onChange: (size: number) => void }) => (
    <label className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-xl border border-slate-200">
        <List size={16} className="text-slate-400" />
        <select
            value={value}
            onChange={(e) => onChange(parseInt(e.target.value, 10))}
            className="bg-transparent font-bold text-slate-900 outline-none text-sm cursor-pointer"
            aria-label="Počet záznamů na stránku"
        >
            {[5, 10, 20, 50].map((size) => (
                <option key={size} value={size}>{size} na stránku</option>
            ))}
        </select>
    </label>
);

interface PaginationProps {
    page: number;
    pageCount: number;
    total?: number;
    onPageChange: (page: number) => void;
}

export const Pagination = ({ page, pageCount, total, onPageChange }: PaginationProps) => {
    const pages = Math.max(1, pageCount);
    return (
        <div className="flex flex-row items-center justify-between px-2 pt-2">
            <p className="text-xs font-bold text-slate-400 tracking-wide uppercase">
                Strana <span className="text-blue-600">{Math.min(page, pages)}</span> z {pages}
                {total !== undefined && <span className="ml-2 normal-case">({total} záznamů)</span>}
            </p>
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    aria-label="Předchozí stránka"
                    onClick={() => onPageChange(page - 1)}
                    disabled={page <= 1}
                    className="p-3 rounded-xl border border-slate-200 text-slate-500 bg-white disabled:opacity-20 hover:bg-slate-50 transition-all active:scale-90 shadow-sm"
                >
                    <ChevronLeft size={20} />
                </button>
                <button
                    type="button"
                    aria-label="Další stránka"
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= pages}
                    className="p-3 rounded-xl border border-slate-200 text-slate-500 bg-white disabled:opacity-20 hover:bg-slate-50 transition-all active:scale-90 shadow-sm"
                >
                    <ChevronRight size={20} />
                </button>
            </div>
        </div>
    );
};

/* --- STATES --- */

export const LoadingState = ({ label = 'Načítám data…' }: { label?: string }) => (
    <div className="py-24 flex flex-col items-center justify-center text-blue-600 font-bold gap-4" role="status">
        <Loader2 className="animate-spin" size={40} />
        <span className="text-sm uppercase tracking-widest">{label}</span>
    </div>
);

export const EmptyState = ({ label }: { label: string }) => (
    <div className="py-16 text-center text-slate-400 font-bold italic border-2 border-dashed border-slate-200 rounded-3xl bg-white">
        {label}
    </div>
);

export const ErrorBanner = ({ message, onRetry }: { message: string; onRetry?: () => void }) => (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-2xl text-sm font-bold" role="alert">
        <span className="flex items-center gap-2"><AlertTriangle size={18} className="shrink-0" /> {message}</span>
        {onRetry && (
            <button type="button" onClick={onRetry} className="underline font-black text-red-700 self-start sm:self-auto">
                Zkusit znovu
            </button>
        )}
    </div>
);

/* --- MODALS --- */

export type MessageType = 'error' | 'success' | 'info';

export interface MessageState {
    title: string;
    text: string;
    type: MessageType;
}

export const MessageModal = ({ message, onClose }: { message: MessageState | null; onClose: () => void }) => {
    if (!message) return null;
    return (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" role="dialog" aria-modal="true">
            <div className="bg-white w-full max-w-sm rounded-[32px] overflow-hidden shadow-2xl p-8 text-center border-t-8 border-slate-900">
                <div className="p-4 rounded-2xl w-fit mx-auto mb-6">
                    {message.type === 'error' && <XCircle className="text-red-500" size={48} />}
                    {message.type === 'success' && <CheckCircle2 className="text-green-500" size={48} />}
                    {message.type === 'info' && <Info className="text-blue-500" size={48} />}
                </div>
                <h3 className="text-2xl font-black text-gray-900 mb-2 tracking-tight">{message.title}</h3>
                <p className="text-gray-500 leading-relaxed font-medium mb-8">{message.text}</p>
                <button
                    type="button"
                    onClick={onClose}
                    className="w-full py-4 rounded-2xl bg-slate-900 text-white font-extrabold uppercase text-xs shadow-lg hover:bg-slate-800 transition-colors"
                >
                    Rozumím
                </button>
            </div>
        </div>
    );
};

interface ConfirmModalProps {
    open: boolean;
    title: string;
    text?: React.ReactNode;
    confirmLabel?: string;
    busy?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

export const ConfirmModal = ({ open, title, text, confirmLabel = 'Smazat', busy = false, onConfirm, onCancel }: ConfirmModalProps) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm" role="dialog" aria-modal="true">
            <div className="bg-white w-full max-w-sm rounded-[32px] overflow-hidden shadow-2xl p-8 text-center">
                <div className="p-4 bg-red-50 rounded-2xl w-fit mx-auto mb-6">
                    <AlertTriangle className="text-red-500" size={32} />
                </div>
                <h3 className="text-2xl font-black text-gray-900 mb-2 tracking-tight">{title}</h3>
                {text && <p className="text-gray-500 leading-relaxed font-medium">{text}</p>}
                <div className="flex gap-3 mt-8">
                    <button type="button" onClick={onCancel} disabled={busy} className="flex-1 py-4 rounded-2xl bg-white border border-gray-200 text-gray-700 font-extrabold uppercase text-xs hover:bg-gray-50 transition-colors">
                        Zrušit
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={busy}
                        className="flex-1 py-4 rounded-2xl bg-red-500 text-white font-extrabold uppercase text-xs shadow-lg shadow-red-200 hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        {busy && <Loader2 size={14} className="animate-spin" />} {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

/* --- FILE RECORDS GRID (documents, contracts) --- */

export interface FileRecordRow {
    id: number;
    documentId: string;
    name: string;
    fileCount: number;
    createdAt: string;
}

interface FileRecordsViewProps {
    rows: FileRecordRow[];
    isAdmin: boolean;
    emptyLabel: string;
    onOpen: (row: FileRecordRow) => void;
    onReplace: (row: FileRecordRow) => void;
    onDelete: (row: FileRecordRow) => void;
}

const formatDate = (iso: string) => (iso ? new Date(iso).toLocaleDateString('cs-CZ') : '');

/** Responsive grid of named records with attached files (table on desktop, cards on mobile). */
export const FileRecordsView = ({ rows, isAdmin, emptyLabel, onOpen, onReplace, onDelete }: FileRecordsViewProps) => {
    const isMobile = useIsMobile();

    if (rows.length === 0) return <EmptyState label={emptyLabel} />;

    const actions = (row: FileRecordRow, large: boolean) => (
        <>
            <AppButton variant="primary" onClick={() => onOpen(row)} disabled={row.fileCount === 0} title={row.fileCount === 0 ? 'Záznam nemá přiložený soubor' : 'Otevřít soubor'} className={large ? 'flex-1 py-3' : 'px-5 py-2 text-sm'}>
                <Eye size={16} /> Otevřít
            </AppButton>
            {isAdmin && (
                <>
                    <AppButton variant="outline" onClick={() => onReplace(row)} title="Nahradit soubor" className={large ? 'py-3 px-4' : 'px-3 py-2 text-sm'}>
                        <Upload size={16} /> {!large && 'Nahradit'}
                    </AppButton>
                    <button type="button" onClick={() => onDelete(row)} aria-label={`Smazat ${row.name}`} className="p-2.5 text-slate-300 hover:text-red-500 transition-all hover:scale-110">
                        <Trash2 size={20} />
                    </button>
                </>
            )}
        </>
    );

    if (isMobile) {
        return (
            <div className="grid grid-cols-1 gap-4">
                {rows.map((row) => (
                    <div key={row.documentId} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                        <div className="flex items-start gap-4">
                            <div className="p-3 bg-slate-50 rounded-xl text-blue-600 shrink-0"><FileText size={24} /></div>
                            <div className="min-w-0 flex-1">
                                <div className="font-bold text-slate-900 text-lg leading-tight truncate">{row.name}</div>
                                <div className="text-xs text-slate-400 font-bold mt-1">{formatDate(row.createdAt)} · souborů: {row.fileCount}</div>
                            </div>
                        </div>
                        <div className="flex gap-2 pt-2 border-t border-slate-50 items-center">{actions(row, true)}</div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full text-left border-collapse">
                <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                        <th className="px-8 py-4 font-bold text-slate-400 text-xs tracking-wider uppercase">Název</th>
                        <th className="px-8 py-4 font-bold text-slate-400 text-xs tracking-wider uppercase">Vloženo</th>
                        <th className="px-8 py-4 font-bold text-slate-400 text-xs tracking-wider text-right uppercase">Akce</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                    {rows.map((row) => (
                        <tr key={row.documentId} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-8 py-5">
                                <div className="font-bold text-slate-900 text-lg leading-tight">{row.name}</div>
                                <div className="text-xs text-slate-400 font-bold mt-1">Souborů: {row.fileCount}</div>
                            </td>
                            <td className="px-8 py-5 text-sm font-bold text-slate-500">{formatDate(row.createdAt)}</td>
                            <td className="px-8 py-5">
                                <div className="flex justify-end gap-3 items-center">{actions(row, false)}</div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

/** Toolbar shared by list modules: refresh, record count, page size and optional create action. */
export const ListToolbar = ({
    total,
    pageSize,
    loading,
    onRefresh,
    onPageSizeChange,
    children,
}: {
    total: number;
    pageSize: number;
    loading: boolean;
    onRefresh: () => void;
    onPageSizeChange: (size: number) => void;
    children?: React.ReactNode;
}) => (
    <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center justify-between bg-white p-4 rounded-2xl border border-slate-200 shadow-sm mb-6">
        <div className="flex flex-1 flex-col lg:flex-row gap-4 lg:items-center">{children}</div>
        <div className="flex items-center gap-3 justify-between lg:justify-end">
            <span className="text-slate-400 font-bold text-sm whitespace-nowrap">
                Záznamů: <span className="text-slate-900">{total}</span>
            </span>
            <PageSizeSelect value={pageSize} onChange={onPageSizeChange} />
            <AppButton variant="outline" onClick={onRefresh} className="py-2 px-4 text-sm" title="Obnovit seznam">
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> <span className="hidden md:inline">Obnovit</span>
            </AppButton>
        </div>
    </div>
);

